/*
 * Copyright (c) 2026.  atagon GmbH.  All rights reserved.
 *
 * This file is part of the atagon codebase and may not be copied,
 *  distributed, or modified without explicit written permission from
 * atagon GmbH. https://www.atagon.com
 */

import {useEffect, useRef, useState} from "react";
import {AnimatePresence, motion} from "motion/react";
import {useNotificationStore} from "../../stores/NotificationStore.js";
import DisplayIcon from "../Icons/DisplayIcon.jsx";
import GlobalPortal from "../GlobalPortal.jsx";
import NotificationsDialog from "./NotificationsDialog.jsx";

const typeStyles = {
    ERROR: {
        iconName: "PiXCircle",
        iconColor: "text-red-500",
        iconBg: "bg-red-50",
        borderColor: "border-l-red-500",
    },
    WARNING: {
        iconName: "PiWarningCircle",
        iconColor: "text-amber-500",
        iconBg: "bg-amber-50",
        borderColor: "border-l-amber-500",
    },
};

function NotificationToastItem({notification, onDismiss}) {
    const style = typeStyles[notification.type] ?? typeStyles.WARNING;

    useEffect(() => {
        const timer = setTimeout(() => onDismiss(notification.id), 6000);
        return () => clearTimeout(timer);
    }, []);

    return (
        <motion.div
            layout
            initial={{opacity: 0, x: 24}}
            animate={{opacity: 1, x: 0}}
            exit={{opacity: 0, x: 24}}
            transition={{duration: 0.3, ease: "easeOut"}}
            className="group relative flex items-start gap-3 w-96 px-2 py-2 bg-white rounded-lg shadow-xl border border-gray-100 overflow-hidden cursor-pointer"
            onClick={() => {
                NotificationsDialog()
            }}
        >
            <div className={`shrink-0 flex items-center justify-center size-6 rounded-full ${style.iconBg}`}>
                <DisplayIcon iconName={style.iconName} className={`size-3 ${style.iconColor}`}/>
            </div>

            <div className="w-0 flex-1 mt-0.5">
                <div className="text-sm font-semibold text-gray-900">{notification.data.title}</div>
                <div className="mt-0.5 text-sm text-gray-600 leading-snug">
                    {notification.data.text}
                    {notification.data.linkShowMe && (
                        <span className="ml-1 text-blue-500 hover:text-blue-600 hover:underline font-medium">
                            {notification.data.linkShowMe}
                        </span>
                    )}
                </div>
            </div>

            <button
                onClick={(e) => {
                    e.stopPropagation();
                    onDismiss(notification.id);
                }}
                className="shrink-0 mt-0.5 text-gray-400 hover:text-gray-600 opacity-0 transition-opacity duration-200 group-hover:opacity-100 z-20"
                aria-label="Dismiss notification"
            >
                <DisplayIcon iconName="PiX" className="size-4"/>
            </button>

            <div
                className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center
                           bg-white/40 backdrop-blur-sm opacity-0 transition-opacity duration-200
                           group-hover:opacity-100"
            >
                <span className="px-3 text-sm font-medium text-gray-900 text-center">
                   View Notification
                </span>
            </div>
        </motion.div>
    );
}

export function NotificationToaster() {
    const notifications = useNotificationStore((state) => state.notifications);

    const [toasts, setToasts] = useState([]);
    const seenIds = useRef(new Set());
    const isInitialized = useRef(false);

    useEffect(() => {
        if (!isInitialized.current) {
            notifications.forEach((n) => seenIds.current.add(n.id));
            isInitialized.current = true;
            return;
        }

        const newAlerts = notifications.filter((n) => !seenIds.current.has(n.id) && (n.type === "ERROR" || n.type === "WARNING"));
        if (newAlerts.length === 0) return;

        newAlerts.forEach((n) => seenIds.current.add(n.id));
        setToasts((prev) => [...prev, ...newAlerts]);
    }, [notifications]);

    const handleDismiss = (id) => setToasts((prev) => prev.filter((t) => t.id !== id));
    
    const visibleToasts = toasts.slice(0, 2);
    const hiddenCount = toasts.length - visibleToasts.length;

    return <GlobalPortal>
        <div className="fixed top-16 right-4 z-50 flex flex-col gap-3 pointer-events-none">
            <AnimatePresence>
                {visibleToasts.map((toast) => (
                    <div key={toast.id} className="pointer-events-auto">
                        <NotificationToastItem notification={toast} onDismiss={handleDismiss}/>
                    </div>
                ))}
                {hiddenCount > 0 && (
                    <motion.div
                        layout
                        initial={{opacity: 0, x: 24}}
                        animate={{opacity: 1, x: 0}}
                        exit={{opacity: 0, x: 24}}
                        transition={{duration: 0.3, ease: "easeOut"}}
                        className="pointer-events-auto flex w-96 px-4 py-2 bg-white rounded-lg shadow-xl border border-gray-100 cursor-pointer hover:bg-gray-50"
                        onClick={() => NotificationsDialog()}
                    >
                        <span className="text-sm font-medium text-gray-700">
                            {hiddenCount} more message{hiddenCount === 1 ? "" : "s"}
                        </span>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    </GlobalPortal>
}